/**
 * SetupPage — User provisioning portal (clutch_admin only)
 *
 * Route: /setup
 *
 * Allows clutch_admin to:
 *   Users tab       — list, provision, and deactivate platform users
 *   Assignments tab — assign / remove clutch_consultant → client mappings
 *
 * Auth: uses Firebase Bearer token via apiFetch; backed by
 *       the require_admin_or_clutch_admin dependency in the API.
 */

import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { apiFetch } from '../lib/apiClient'
import styles from './SetupPage.module.css'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PlatformUser {
  id:           number
  firebase_uid: string | null
  email:        string
  display_name: string | null
  role:         'clutch_admin' | 'clutch_consultant' | 'client_admin'
  client_id:    number | null
  client_name:  string | null
  active_flag:  boolean
}

interface ClientOption {
  client_id:   number
  client_name: string
}

interface Assignment {
  client_id:   number
  client_name: string
}

// Provision form only allows these two — clutch_admin is elevated separately
const ROLES = [
  { value: 'clutch_consultant', label: 'Consultant' },
  { value: 'client_admin',      label: 'Client Admin' },
]

const ROLE_BADGE: Record<string, string> = {
  clutch_admin:      styles.roleAdmin,
  clutch_consultant: styles.roleConsultant,
  client_admin:      styles.roleClient,
}

const ROLE_LABEL: Record<string, string> = {
  clutch_admin:      'Admin',
  clutch_consultant: 'Consultant',
  client_admin:      'Client',
}

type Tab = 'users' | 'assignments'

// ---------------------------------------------------------------------------
// SetupPage
// ---------------------------------------------------------------------------

export function SetupPage() {
  const { idToken } = useAuth()
  const [tab, setTab] = useState<Tab>('users')

  // -- Users state --
  const [users, setUsers]       = useState<PlatformUser[]>([])
  const [clients, setClients]   = useState<ClientOption[]>([])
  const [usersLoading, setUsersLoading] = useState(true)
  const [usersError, setUsersError]     = useState<string | null>(null)

  // -- Provision form --
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({
    email:        '',
    display_name: '',
    role:         'clutch_consultant',
    client_id:    '',
  })
  const [formError, setFormError]   = useState<string | null>(null)
  const [formBusy, setFormBusy]     = useState(false)
  const [formSuccess, setFormSuccess] = useState<string | null>(null)

  // -- Role elevation --
  const [elevatingId, setElevatingId] = useState<number | null>(null)

  // -- Assignments state --
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [assignments, setAssignments]       = useState<Assignment[]>([])
  const [assignClient, setAssignClient]     = useState('')
  const [assignBusy, setAssignBusy]         = useState(false)
  const [assignError, setAssignError]       = useState<string | null>(null)
  const [assignSuccess, setAssignSuccess]   = useState<string | null>(null)

  // ---------------------------------------------------------------------------
  // Load users + clients
  // ---------------------------------------------------------------------------

  const loadData = useCallback(async () => {
    if (!idToken) return
    setUsersLoading(true)
    setUsersError(null)
    try {
      const [usersData, clientsData] = await Promise.all([
        apiFetch<PlatformUser[]>('/auth/users', idToken),
        apiFetch<ClientOption[]>('/auth/clients', idToken),
      ])
      setUsers(usersData)
      setClients(clientsData)
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Failed to load data.')
    } finally {
      setUsersLoading(false)
    }
  }, [idToken])

  useEffect(() => { loadData() }, [loadData])

  // ---------------------------------------------------------------------------
  // Provision form handlers
  // ---------------------------------------------------------------------------

  function handleFormChange(field: string, value: string) {
    setForm(f => ({ ...f, [field]: value }))
    setFormError(null)
    setFormSuccess(null)
  }

  async function handleProvision(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)
    setFormSuccess(null)

    if (!form.email.trim()) { setFormError('Email is required.'); return }
    if (!form.role)         { setFormError('Role is required.'); return }
    if (form.role === 'client_admin' && !form.client_id) {
      setFormError('Client is required for Client Admin role.'); return
    }

    setFormBusy(true)
    try {
      const body: Record<string, unknown> = {
        email:        form.email.trim().toLowerCase(),
        display_name: form.display_name.trim() || null,
        role:         form.role,
        client_id:    form.role === 'client_admin' ? Number(form.client_id) : null,
      }
      await apiFetch('/auth/users', idToken, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      setFormSuccess(`${form.email} provisioned as ${ROLE_LABEL[form.role]}. They can sign in now.`)
      setForm({ email: '', display_name: '', role: 'clutch_consultant', client_id: '' })
      setShowForm(false)
      await loadData()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to provision user.')
    } finally {
      setFormBusy(false)
    }
  }

  async function handleDeactivate(userId: number, email: string) {
    if (!window.confirm(`Deactivate ${email}? They will lose platform access immediately.`)) return
    try {
      await apiFetch(`/auth/users/${userId}`, idToken, { method: 'DELETE' })
      await loadData()
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Failed to deactivate user.')
    }
  }

  async function handleElevate(userId: number, email: string) {
    if (!window.confirm(`Promote ${email} to Principal Consultant (clutch_admin)? This grants full admin access.`)) return
    setElevatingId(userId)
    try {
      await apiFetch(`/auth/users/${userId}/role`, idToken, {
        method: 'PATCH',
        body: JSON.stringify({ role: 'clutch_admin' }),
      })
      await loadData()
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Failed to elevate user.')
    } finally {
      setElevatingId(null)
    }
  }

  // ---------------------------------------------------------------------------
  // Assignments handlers
  // ---------------------------------------------------------------------------

  const loadAssignments = useCallback(async (userId: number) => {
    if (!idToken) return
    setAssignError(null)
    try {
      const data = await apiFetch<Assignment[]>(`/auth/consultant-assignments/${userId}`, idToken)
      setAssignments(data)
    } catch {
      setAssignments([])
    }
  }, [idToken])

  useEffect(() => {
    if (selectedUserId !== null) loadAssignments(selectedUserId)
  }, [selectedUserId, loadAssignments])

  async function handleAssign(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedUserId || !assignClient) return
    setAssignBusy(true)
    setAssignError(null)
    setAssignSuccess(null)
    try {
      await apiFetch('/auth/consultant-assignments', idToken, {
        method: 'POST',
        body: JSON.stringify({ user_id: selectedUserId, client_id: Number(assignClient) }),
      })
      setAssignClient('')
      setAssignSuccess('Assignment added.')
      await loadAssignments(selectedUserId)
    } catch (err) {
      setAssignError(err instanceof Error ? err.message : 'Failed to add assignment.')
    } finally {
      setAssignBusy(false)
    }
  }

  async function handleRemoveAssignment(clientId: number) {
    if (!selectedUserId) return
    setAssignBusy(true)
    setAssignError(null)
    try {
      await apiFetch('/auth/consultant-assignments', idToken, {
        method: 'DELETE',
        body: JSON.stringify({ user_id: selectedUserId, client_id: clientId }),
      })
      await loadAssignments(selectedUserId)
    } catch (err) {
      setAssignError(err instanceof Error ? err.message : 'Failed to remove assignment.')
    } finally {
      setAssignBusy(false)
    }
  }

  // Consultants for assignments tab
  const consultants = users.filter(u => u.role === 'clutch_consultant' && u.active_flag)
  const selectedUser = users.find(u => u.id === selectedUserId) ?? null

  // Already-assigned client IDs (for filtering picker)
  const assignedClientIds = new Set(assignments.map(a => a.client_id))
  const availableClients = clients.filter(c => !assignedClientIds.has(c.client_id))

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <main className={styles.page}>
      <div className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Principal Consultant</p>
          <h1 className={styles.title}>Platform Setup</h1>
          <p className={styles.subtitle}>Provision and manage platform users</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className={styles.tabBar} role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'users'}
          className={`${styles.tab} ${tab === 'users' ? styles.tabActive : ''}`}
          onClick={() => setTab('users')}
          type="button"
        >
          Users
        </button>
        <button
          role="tab"
          aria-selected={tab === 'assignments'}
          className={`${styles.tab} ${tab === 'assignments' ? styles.tabActive : ''}`}
          onClick={() => setTab('assignments')}
          type="button"
        >
          Consultant Assignments
        </button>
      </div>

      {/* ─── Users tab ─────────────────────────────────────────────────── */}
      {tab === 'users' && (
        <section aria-label="Users">
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Platform Users</h2>
            <button
              className={`btn btn-aqua ${styles.addBtn}`}
              onClick={() => { setShowForm(v => !v); setFormError(null); setFormSuccess(null) }}
              type="button"
              aria-expanded={showForm}
            >
              {showForm ? '✕ Cancel' : '+ Invite user'}
            </button>
          </div>

          {formSuccess && <div className={styles.successBanner} role="status">{formSuccess}</div>}
          {usersError  && <div className={styles.errorBanner}   role="alert">{usersError}</div>}

          {/* Provision form */}
          {showForm && (
            <form className={styles.form} onSubmit={handleProvision} noValidate>
              <h3 className={styles.formTitle}>Invite User</h3>
              <p className={styles.formHint}>
                Enter the user's work email. They'll be able to sign in immediately —
                no Firebase Console steps needed.
              </p>

              <div className={styles.fields}>
                <label className={styles.field}>
                  <span className={styles.label}>Email *</span>
                  <input
                    className={styles.input}
                    type="email"
                    value={form.email}
                    onChange={e => handleFormChange('email', e.target.value)}
                    placeholder="user@example.com"
                    aria-required="true"
                    autoComplete="email"
                  />
                </label>

                <label className={styles.field}>
                  <span className={styles.label}>Display name</span>
                  <input
                    className={styles.input}
                    type="text"
                    value={form.display_name}
                    onChange={e => handleFormChange('display_name', e.target.value)}
                    placeholder="Full name (optional)"
                  />
                </label>

                <label className={styles.field}>
                  <span className={styles.label}>Role *</span>
                  <select
                    className={styles.select}
                    value={form.role}
                    onChange={e => handleFormChange('role', e.target.value)}
                    aria-required="true"
                  >
                    {ROLES.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </label>

                {form.role === 'client_admin' && (
                  <label className={styles.field}>
                    <span className={styles.label}>Client *</span>
                    <select
                      className={styles.select}
                      value={form.client_id}
                      onChange={e => handleFormChange('client_id', e.target.value)}
                      aria-required="true"
                    >
                      <option value="">— select client —</option>
                      {clients.map(c => (
                        <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>

              {formError && <div className={styles.formError} role="alert">{formError}</div>}

              <div className={styles.formActions}>
                <button
                  className="btn btn-aqua"
                  type="submit"
                  disabled={formBusy}
                  aria-busy={formBusy}
                >
                  {formBusy ? 'Saving…' : 'Add user'}
                </button>
                <p className={styles.formNote}>
                  To grant full admin access, add the user as Consultant first, then use
                  "Elevate to Admin" in the user table.
                </p>
              </div>
            </form>
          )}

          {/* Users table */}
          {usersLoading ? (
            <div className={styles.loadingMsg} aria-live="polite">Loading users…</div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">Email</th>
                    <th scope="col">Name</th>
                    <th scope="col">Role</th>
                    <th scope="col">Client scope</th>
                    <th scope="col">Status</th>
                    <th scope="col"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={6} className={styles.emptyRow}>No users provisioned yet.</td>
                    </tr>
                  ) : users.map(u => (
                    <tr key={u.id} className={!u.active_flag ? styles.inactiveRow : ''}>
                      <td className={styles.emailCell}>
                        {u.email}
                        {!u.firebase_uid && u.active_flag && (
                          <span className={styles.pendingBadge} title="Has not signed in yet">pending</span>
                        )}
                      </td>
                      <td>{u.display_name ?? <span className={styles.muted}>—</span>}</td>
                      <td>
                        <span className={`${styles.rolePill} ${ROLE_BADGE[u.role] ?? ''}`}>
                          {ROLE_LABEL[u.role] ?? u.role}
                        </span>
                      </td>
                      <td>{u.client_name ?? <span className={styles.muted}>All</span>}</td>
                      <td>
                        <span className={u.active_flag ? styles.statusActive : styles.statusInactive}>
                          {u.active_flag ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className={styles.actionCell}>
                        {u.active_flag && u.role === 'clutch_consultant' && (
                          <button
                            className={styles.elevateBtn}
                            onClick={() => handleElevate(u.id, u.email)}
                            disabled={elevatingId === u.id}
                            type="button"
                            aria-label={`Elevate ${u.email} to Principal Consultant`}
                          >
                            {elevatingId === u.id ? '…' : 'Elevate to Admin'}
                          </button>
                        )}
                        {u.active_flag && (
                          <button
                            className={styles.deactivateBtn}
                            onClick={() => handleDeactivate(u.id, u.email)}
                            type="button"
                            aria-label={`Deactivate ${u.email}`}
                          >
                            Deactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ─── Assignments tab ───────────────────────────────────────────── */}
      {tab === 'assignments' && (
        <section aria-label="Consultant assignments">
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Consultant → Client Assignments</h2>
          </div>
          <p className={styles.assignHint}>
            Consultants are only shown data for their assigned clients.
            Admins see all clients by default.
          </p>

          {usersLoading ? (
            <div className={styles.loadingMsg}>Loading…</div>
          ) : (
            <div className={styles.assignLayout}>
              {/* Left: consultant picker */}
              <div className={styles.consultantList} role="listbox" aria-label="Select a consultant">
                <p className={styles.sideLabel}>Consultants</p>
                {consultants.length === 0 ? (
                  <p className={styles.muted}>No active consultants found.</p>
                ) : consultants.map(u => (
                  <button
                    key={u.id}
                    role="option"
                    aria-selected={selectedUserId === u.id}
                    className={`${styles.consultantItem} ${selectedUserId === u.id ? styles.consultantItemActive : ''}`}
                    onClick={() => { setSelectedUserId(u.id); setAssignError(null); setAssignSuccess(null) }}
                    type="button"
                  >
                    <span className={styles.consultantEmail}>{u.email}</span>
                    {u.display_name && <span className={styles.consultantName}>{u.display_name}</span>}
                  </button>
                ))}
              </div>

              {/* Right: assignments for selected consultant */}
              <div className={styles.assignPanel}>
                {selectedUser === null ? (
                  <p className={styles.muted}>Select a consultant to manage their client assignments.</p>
                ) : (
                  <>
                    <h3 className={styles.assignPanelTitle}>
                      {selectedUser.display_name ?? selectedUser.email}
                    </h3>

                    {assignError   && <div className={styles.errorBanner}   role="alert">{assignError}</div>}
                    {assignSuccess && <div className={styles.successBanner} role="status">{assignSuccess}</div>}

                    {/* Existing assignments */}
                    <div className={styles.assignedList}>
                      {assignments.length === 0 ? (
                        <p className={styles.muted}>No client assignments. Add one below.</p>
                      ) : assignments.map(a => (
                        <div key={a.client_id} className={styles.assignedItem}>
                          <span>{a.client_name}</span>
                          <button
                            className={styles.removeBtn}
                            onClick={() => handleRemoveAssignment(a.client_id)}
                            disabled={assignBusy}
                            type="button"
                            aria-label={`Remove ${a.client_name} from ${selectedUser.email}`}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Add assignment form */}
                    {availableClients.length > 0 && (
                      <form className={styles.addAssignForm} onSubmit={handleAssign}>
                        <label className={styles.field}>
                          <span className={styles.label}>Add client</span>
                          <select
                            className={styles.select}
                            value={assignClient}
                            onChange={e => setAssignClient(e.target.value)}
                            aria-label="Select client to assign"
                          >
                            <option value="">— select client —</option>
                            {availableClients.map(c => (
                              <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
                            ))}
                          </select>
                        </label>
                        <button
                          className="btn btn-aqua"
                          type="submit"
                          disabled={assignBusy || !assignClient}
                          aria-busy={assignBusy}
                        >
                          {assignBusy ? 'Adding…' : 'Add'}
                        </button>
                      </form>
                    )}

                    {availableClients.length === 0 && clients.length > 0 && (
                      <p className={styles.allAssigned}>
                        All clients are already assigned to this consultant.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </section>
      )}
    </main>
  )
}
